import { BaseEffect, EasingFunction, Easing, CanvasDimensions, EffectResult, MultiTrackEffect, LayerType } from './BaseEffect';

/**
 * PerLayerStaticBlurEffect applies a constant blur value using per-layer rendering.
 * 
 * ARCHITECTURE: Per-layer effect - each layer gets individual static blur styling applied directly.
 * Unlike PerLayerBlurEffect, this does NOT animate the blur - it applies instantly and removes instantly.
 * 
 * This is ideal for static blur effects because:
 * - More performant (no animation calculations)
 * - Each layer can have different blur amounts
 * - Better GPU performance with individual filters
 * - No container nesting performance overhead
 * - Perfect for creating distinct blurred sections in timeline
 * 
 * @param blurAmount - Blur value in pixels (applied instantly, not animated)
 * @param duration - How long the blur effect lasts in seconds
 * @param startAt - When the blur effect starts in the timeline (seconds)
 * @param targetLayers - Array of layer types to apply the effect to
 * @param excludeLayers - Optional array of layer types to exclude from the effect
 */
export class PerLayerStaticBlurEffect extends BaseEffect implements MultiTrackEffect {
    blurAmount: number; // Static blur value in pixels
    targetLayers: LayerType[];
    excludeLayers: LayerType[];
    containerBased: boolean = false; // Per-layer: blur applied to individual layers for better performance

    constructor(config: {
        blurAmount: number;
        duration: number;
        startAt: number;
        layers: LayerType[]; // Explicit layer selection
    }) {
        // Static blur doesn't need easing since there's no animation
        super('per-layer-static-blur', config.duration, config.startAt, Easing.linear, true);
        
        // Validate and assign blur amount
        this.blurAmount = (typeof config.blurAmount === 'number' && !isNaN(config.blurAmount)) 
            ? Math.max(0, config.blurAmount) // Ensure non-negative
            : 0;
        
        // Use explicit layer selection
        this.targetLayers = config.layers || [];
        this.excludeLayers = []; // Not needed with explicit layer selection
        
        console.log(`[STATIC BLUR DEBUG] PerLayerStaticBlurEffect created: blurAmount=${this.blurAmount}px, duration=${config.duration}s, startAt=${config.startAt}s, layers=[${this.targetLayers.join(', ')}]`);
    }

    // Required by Effect interface - not used for multi-track effects
    apply(
        ctx: CanvasRenderingContext2D,
        dimensions: CanvasDimensions,
        progress: number,
        prev: EffectResult
    ): EffectResult {
        // Per-layer effects don't use canvas operations - they use CSS transforms
        return prev;
    }

    // Primary method for static blur - returns constant blur or none
    getContainerStyle(
        progress: number,
        dimensions?: CanvasDimensions,
        effectResult?: EffectResult
    ): React.CSSProperties {
        // Simple binary logic: blur is either on or off
        const isActive = progress > 0 && progress <= 1;
        
        if (!isActive) {
            // Effect is not active - no blur
            return {};
        }
        
        // Effect is active - apply full blur amount
        console.log(`[STATIC BLUR DEBUG] ${this.type} ACTIVE: applying ${this.blurAmount}px blur at ${this.at}s + ${(progress * this.duration).toFixed(2)}s`);
        
        return {
            filter: `blur(${this.blurAmount}px)`,
            transition: 'none' // Prevent any CSS transitions for instant application
        };
    }

    // Helper method to check if a layer should be affected by this effect
    shouldApplyToLayer(layerType: LayerType): boolean {
        // Simple: only apply to explicitly selected layers
        const shouldApply = this.targetLayers.includes(layerType);
        console.log(`[STATIC BLUR DEBUG] ${this.type} layer ${layerType}: shouldApply=${shouldApply}, targetLayers=[${this.targetLayers.join(', ')}]`);
        return shouldApply;
    }

    // Static factory methods for common use cases

    /**
     * Creates static blur effect for background and scene only
     * Perfect for blurring video content while keeping all text/UI sharp
     */
    static forBackgroundAndScene(
        blurAmount: number,
        duration: number,
        startAt: number
    ): PerLayerStaticBlurEffect {
        return new PerLayerStaticBlurEffect({
            blurAmount,
            duration,
            startAt,
            layers: ['background', 'scene']
        });
    }

    /**
     * Creates static blur effect for background only
     * Only blurs background images/videos, keeps everything else sharp
     */
    static forBackgroundOnly(
        blurAmount: number,
        duration: number,
        startAt: number
    ): PerLayerStaticBlurEffect {
        return new PerLayerStaticBlurEffect({
            blurAmount,
            duration,
            startAt,
            layers: ['background']
        });
    }

    /**
     * Creates static blur effect for scene (main video) only
     * Only blurs the main video content, keeps everything else sharp
     */
    static forSceneOnly(
        blurAmount: number,
        duration: number,
        startAt: number
    ): PerLayerStaticBlurEffect {
        return new PerLayerStaticBlurEffect({
            blurAmount,
            duration,
            startAt,
            layers: ['scene']
        });
    }

    /**
     * Creates static blur effect for text overlays only
     * Only blurs text content while keeping video sharp
     */
    static forTextOnly(
        blurAmount: number,
        duration: number,
        startAt: number
    ): PerLayerStaticBlurEffect {
        return new PerLayerStaticBlurEffect({
            blurAmount,
            duration,
            startAt,
            layers: ['textOverlay', 'caption']
        });
    }

    /**
     * Creates static blur effect for all layers
     * Blurs everything including text and branding
     */
    static forAllLayers(
        blurAmount: number,
        duration: number,
        startAt: number
    ): PerLayerStaticBlurEffect {
        return new PerLayerStaticBlurEffect({
            blurAmount,
            duration,
            startAt,
            layers: ['background', 'scene', 'overlay', 'overlayTransition', 'textOverlay', 'caption', 'branding']
        });
    }

    /**
     * Creates static blur effect for custom layer selection
     * Most flexible - specify exactly which layers to blur
     */
    static forLayers(
        layers: LayerType[],
        blurAmount: number,
        duration: number,
        startAt: number
    ): PerLayerStaticBlurEffect {
        return new PerLayerStaticBlurEffect({
            blurAmount,
            duration,
            startAt,
            layers
        });
    }
}

// Export with old name for backward compatibility during transition
export { PerLayerStaticBlurEffect as MultiTrackStaticBlurEffect };